import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef
} from '@angular/core';
import {DragulaService} from 'ng2-dragula';
import {Subject} from 'rxjs/Subject';
import {findIndex, get, isEmpty, isNil, uniqBy} from 'lodash';
import {takeUntil} from 'rxjs/operators';
import {IListField} from '@ispm/shared/interfaces/list-field.interface';
import {IdynamicFormItem} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import {ResolvableComponent} from '@ispm/core/decorators/resolvable-component.decorator';
import {IWidgetItem} from '@ispm/widgets/interfaces/widget-item.interface';
import {DynamicFormItem} from '@ispm/dynamic-form/classes/dynamic-form-item.class';
import {ITEMS_TYPES} from '@ispm/dynamic-form/constants/ItemsTypes';
import {FormGroup} from '@angular/forms';

interface ISelectionListConf {
  edit?: boolean;
  counter?: boolean;
  fields: any[];
  publishSelection?: boolean;
  listItemCss?: string;
}

@Component({
  selector: 'gen-selection-list',
  templateUrl: './selection-list.component.html',
  styleUrls: ['./selection-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@ResolvableComponent()
export class SelectionListComponent implements OnInit, OnDestroy, IWidgetItem {

  // initialize the base number (as current time milliseconds) for generating unique object's ID
  private static baseIdCounter: number = performance.now();

  // the property that is associated to each element with the auto-generated unique object's ID. This property is not
  //  displayed when object is stringified
  private static UNIQUE_ID_PROPERTY_NAME = '__gen-selection-list__unique-item-id__';
  _emptyMessageDisplay: string = '';

  widgetFormInstance: FormGroup;

  @Input()
  trackByField: string = 'id';
  @Input()
  listName: string;

  @Input()
  edit: boolean;

  // indicates if to disable the selection-list component
  @Input()
  disabled: boolean = false;

  @Input()
  prioritizeAvailable: boolean = false;

  @Input() deleteItemAvailable: boolean = false;

  @Input()
  counter: boolean = false;

  @Input()
  set emptyMessageDisplay(message: string) {
    this._emptyMessageDisplay = message === 'Unknown' ? 'Default_Unknown'  : message;
  }


  @Input()
  showTitlesIfEmpty: boolean = true;

  @Input()
  hideTitles: boolean = false;

  @Input() formVertical: boolean = false;

  @Input()
  listModel: any[];

  @Input()
  fields: Array<Partial<IListField>> = [];

  @Input() listType: 'manual-add-list' | null;
  @Input() listRowType: 'content' | null;

  @Output() deleteActionEmit: EventEmitter<string> = new EventEmitter();
  @Output() listOrderChangedEmit: EventEmitter<any[]> = new EventEmitter();
  @Output() addActionEmit: EventEmitter<any> = new EventEmitter();

  addWidgetFormControl: (config: IdynamicFormItem) => void;
  public manualIsActive: boolean;
  private destroyer$: Subject<null> = new Subject<null>();
  widgetUpdateValueCallback: (value) => void;

  @ContentChild(TemplateRef, {static: false}) template: TemplateRef<any>;
  private selectedItem: any;
  private publishSelection: any;
  @Input()
  public listItemCss: string;
  private defaultSelectionLast:boolean = false;

  set (widgetConfig: IdynamicFormItem<ISelectionListConf>) {
    this.edit = widgetConfig.widgetPayload.edit;
    this.counter = widgetConfig.widgetPayload.counter || this.counter;
    this.prioritizeAvailable = this.counter;
    this.fields = widgetConfig.widgetPayload.fields;
    this.publishSelection = widgetConfig.widgetPayload.publishSelection;
    this.defaultSelectionLast = get(widgetConfig.widgetPayload, 'defaultSelectionLast', false);
    this.listItemCss = widgetConfig.widgetPayload.listItemCss;
  }

  set widgetConfig(widgetConfig: IdynamicFormItem) {
    this.set(widgetConfig);
  }

  set widgetData(widgetData: any) {
    if (Array.isArray(widgetData)) {
      const iteratee: string = 'id';
      this.listModel = uniqBy(widgetData, iteratee);

      if (!!this.defaultSelectionLast) {
        this.onClick(this.listModel.length - 1);
      } else {
        let idx: number = -1;
        const ctrlname: string = '_selectedItem';
        if (!isEmpty(this.widgetFormInstance.controls[ctrlname].value)) {
          const curationId = this.widgetFormInstance.controls[ctrlname].value[0].curationId;
          idx = findIndex(this.listModel, item => item.id === curationId);
        }
        this.onClick(idx === -1 ? this.listModel.length - 1 : idx);
      }
      if (this.listModel.length === 0) {
        this.changeDetector.markForCheck();
        this.changeDetector.detectChanges();
      }
    }
  }

  constructor(private dragulaService: DragulaService, private changeDetector: ChangeDetectorRef) { }

  ngOnInit() {
    // set a unique list's name to ensure no duplications in dragulaService's bag list
    if (! this.listName) {
      this.listName = 'destination_' + SelectionListComponent.baseIdCounter++;
    }

    // to use the dragulaService, it is required to call the setOptions method, specifying the container and providing
    // an object literal with your desired options
    if (! this.dragulaService.find(this.listName)) { // need to search first that name is not already added to avoid an error
      this.dragulaService.setOptions(this.listName, { });
    }

    this.dragulaService.dropModel
      .pipe(
        takeUntil(this.destroyer$.asObservable())
      )
      .subscribe(value => {
        const [changedListName] = value;

        if (changedListName === this.listName) {
          this.listOrderChangedEmit.emit(this.listModel);
        }
      });
  }

  deleteEmit(data) {
    this.deleteActionEmit.emit(data);
  }

  addItemEmit() {
    this.manualIsActive = !this.manualIsActive;
    this.addActionEmit.emit();
  }

  trackByFactory() {
    return this.trackByFn.bind(this);
  }

  trackByFn(index: number, item: any) {

    if (!item || typeof item !== 'object') {
      return index;
    }

    let trackId;
    // wrong items order after reordering the list if not all list items have a property, defined for tracking
    // (in this case some items are tracked by one property, some by another one - see code below).
    // so let's check if this is the case:
    if (this.listModel.every(itm => itm[this.trackByField])) {
      // if all items have the property - track by defined property, else continue
      return get(item, this.trackByField);
    }

    // get if a value exists by the default property
    const fallbackPath = get(this.fields, '[0].path');
    trackId = get(item, fallbackPath);
    if (trackId !== undefined && trackId !== null) {
      return trackId;
    }

    // get if a value exists by the auto-generated unique item ID property
    trackId = get(item, SelectionListComponent.UNIQUE_ID_PROPERTY_NAME);
    if (trackId !== undefined && trackId !== null) {
      return trackId;
    }

    // no property found - increment counter to auto-generate a unique ID and associate it to the list item
    const genUniqueId = SelectionListComponent.baseIdCounter++;
    this.associateUniqueItemIdentifier(item, genUniqueId);

    return genUniqueId;
  }

  ngOnDestroy(): void {
    // remove the current list from the dragulaService's bag list
    if (this.dragulaService.find(this.listName)) { // need to search first that name exists to avoid an error
      this.dragulaService.destroy(this.listName);
    }

    this.destroyer$.next();
    this.destroyer$.complete();
  }

  hasInlineAction() {
    return !this.disabled && (this.prioritizeAvailable || this.deleteItemAvailable);
  }

  isListDraggable() {
    return this.prioritizeAvailable && !this.disabled;
  }

  private associateUniqueItemIdentifier(item: any, genUniqueId) {
    // associate a temporary property to the list item (indicate NOT to show this property during enumeration of the
    //  properties on the corresponding object)
    Object.defineProperties(item, {
      [SelectionListComponent.UNIQUE_ID_PROPERTY_NAME]: {
        value: genUniqueId,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
  }

  private setSelection(selectedItem: any) {
    this.selectedItem = selectedItem;
    const selectControlName = '_selectedItem';
    if (isEmpty(this.widgetFormInstance.controls[selectControlName])) {
      this.addWidgetFormControl(DynamicFormItem.builder('', selectControlName, ITEMS_TYPES.TEXT).setCssClass('hidden-form-item'));
    }
    this.widgetFormInstance.controls[selectControlName].patchValue( [selectedItem]);
    this.widgetUpdateValueCallback.apply( this, [this.listModel]);
  }

  onClick(i: number) {
    if (this.publishSelection && i >= 0 && this.listModel.length > i) {
      this.setSelection(this.listModel[i]);
      this.changeDetector.markForCheck();
      this.changeDetector.detectChanges();
    }
  }

  getCssClass(itemId) {
    let res = this.counter ? 'counter ' : '' ;
    res = isNil(this.listItemCss) ? res :  res +  ' ' + this.listItemCss;
    if (this.selectedItem) {
      res += this.selectedItem.id == itemId ? ' active' : '';
    }
    return res;
  }
}
